// src/pages/HistoryPage.js
import React, { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import axios from 'axios'

function HistoryPage() {
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)

  // Logged-in user
  const user = JSON.parse(localStorage.getItem('user')) || {}

  // 1) Consume language from the parent layout via Outlet context
  const { lang } = useOutletContext()

  // 2) Basic translations
  const translations = {
    en: {
      pageTitle: 'Transaction History',
      loading: 'Loading history...',
      pleaseLogin: 'Please log in first.',
      noTransactions: 'No transactions in the past 10 days.',
      tableHeaders: {
        type: 'Transaction Type',
        date: 'Date',
        user: 'User',
        amount: 'Amount',
      },
    },
    ja: {
      pageTitle: '取引履歴',
      loading: '履歴を読み込み中...',
      pleaseLogin: '最初にログインしてください。',
      noTransactions: '過去10日間で取引はありません。',
      tableHeaders: {
        type: '取引の種類',
        date: '日時',
        user: '相手ユーザー',
        amount: '金額',
      },
    },
    pt: {
      pageTitle: 'Histórico de Transações',
      loading: 'Carregando histórico...',
      pleaseLogin: 'Por favor, faça login primeiro.',
      noTransactions: 'Nenhuma transação nos últimos 10 dias.',
      tableHeaders: {
        type: 'Tipo de Transação',
        date: 'Data',
        user: 'Usuário',
        amount: 'Valor',
      },
    },
  }

  const t = translations[lang] || translations.en

  useEffect(() => {
    if (user.id) {
      fetchHistory(user.id)
    } else {
      setLoading(false)
    }
    // eslint-disable-next-line
  }, [])

  const fetchHistory = async (userId) => {
    try {
      // Adjust the endpoint if needed to include pending or requires_payment transactions.
      const res = await axios.get(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/history/${userId}?days=10`
      )
      setTransactions(res.data)
    } catch (error) {
      console.error('Error fetching history:', error)
    }
    setLoading(false)
  }

  if (loading) {
    return <div>{t.loading}</div>
  }

  if (!user.id) {
    return <div>{t.pleaseLogin}</div>
  }

  // Helper to format date
  const formatDate = (isoString) => {
    // Convert to local time. Show yyyy-mm-dd + hh:mm (no seconds)
    // e.g. "2025-07-12 14:05"
    const dateObj = new Date(isoString)
    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const day = String(dateObj.getDate()).padStart(2, '0')
    const hours = String(dateObj.getHours()).padStart(2, '0')
    const minutes = String(dateObj.getMinutes()).padStart(2, '0')

    return `${year}-${month}-${day} ${hours}:${minutes}`
  }

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-4">{t.pageTitle}</h2>

      {transactions.length === 0 ? (
        <p>{t.noTransactions}</p>
      ) : (
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100">
              <tr>
                <th scope="col" className="px-4 py-3">
                  {t.tableHeaders.type}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t.tableHeaders.date}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t.tableHeaders.user}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t.tableHeaders.amount}
                </th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, idx) => {
                // Determine the row type
                // If the API returns tx.status==='pending' or tx.status==='requires_payment',
                // we label them as "Payment Requested".
                let rowType = tx.type
                if (
                  tx.status === 'pending' ||
                  tx.status === 'requires_payment'
                ) {
                  rowType = 'Payment Requested'
                }

                // Remove 6% fee from "Money Sent" or "Money Received"
                // but not from "Payment Requested" by default
                const shouldRemoveFee =
                  rowType === 'Money Sent' || rowType === 'Money Received'

                const displayAmount = shouldRemoveFee
                  ? Math.round(tx.amount / 1.06)
                  : Math.round(tx.amount)

                return (
                  <tr
                    key={idx}
                    className="border-b bg-white hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {rowType}
                    </td>
                    <td className="px-4 py-3">
                      {formatDate(tx.date)}
                    </td>
                    <td className="px-4 py-3">{tx.otherUserName}</td>
                    <td className="px-4 py-3">¥{displayAmount}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default HistoryPage
